import {
  combineLatestTyped,
  IRect,
  IRectType,
  mergeTyped,
  roundRectToInt,
  toAbsoluteRect,
  useDataStream,
  useEventAsStream,
  useStreamEffect,
  useStreamValue,
  withLatestValuesFrom,
  filterNotUndefined
} from 'annotation-tools/utils';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { updateModelImagePartially } from '../../../../api/fetch-functions/model-image';
import { token$ } from '../../../../global-state/auth-state';
import { fetchModelImageListOf } from '../../../../global-state/database/model-image-list';
import { imageId$, modelId$ } from '../../../../global-state/router-id';
import { IImageInfoType } from '../../../../types/record/image-info';

type CropRectReducerState = {
  lastOp: 'canvas' | 'input';
  data: IRectType;
};

export const useCropRect = (
  cropRectRelativeInput$: Observable<IRectType | undefined>,
  imageInfo$: Observable<IImageInfoType>,
  setPostResultType: (value: 'error' | 'success') => void
) => {
  const [
    // dummy comment to control prettier
    cropRectRelativeFromCanvas$,
    setCropRectRelativeFromCanvas
  ] = useEventAsStream<IRectType>();

  const cropRectRelativeMerged$ = useDataStream<
    CropRectReducerState | undefined
  >(
    undefined,
    mergeTyped(
      combineLatestTyped(
        cropRectRelativeInput$.pipe(filterNotUndefined()),
        imageId$ // ページ遷移時にinputで初期化
      ).pipe(map(([a]) => ({ lastOp: 'input' as const, data: a }))),
      cropRectRelativeFromCanvas$.pipe(
        map(a => ({ lastOp: 'canvas' as const, data: a }))
      )
    )
  );

  useStreamEffect(
    cropRectRelativeFromCanvas$.pipe(
      withLatestValuesFrom(token$, modelId$, imageInfo$)
    ),
    ([newCropRect, [token, modelId, image]]) => {
      if (token === undefined) return;
      updateModelImagePartially(token)(
        modelId,
        image.id
      )({
        cropRect: roundRectToInt(toAbsoluteRect(IRect(image.size))(newCropRect))
      }).then(res => {
        setPostResultType(res.type);
        fetchModelImageListOf(modelId);
      });
    }
  );

  const cropRectRelative$ = useDataStream<IRectType | undefined>(
    undefined,
    cropRectRelativeMerged$.pipe(map(a => a?.data))
  );

  const cropRectRelative = useStreamValue(cropRectRelative$);

  return {
    cropRectRelativeFromCanvas$,
    setCropRectRelativeFromCanvas,
    cropRectRelative$,
    cropRectRelative
  };
};
